import { Controller } from "@hotwired/stimulus"

/**
 * Keeps a donut chart's center counter in sync with the VISIBLE (non-hidden)
 * slices — it is the SUM OF THE VISIBLE SLICES. On the dashboard donut that is
 * the PER-ACTIVATION total (labeled "Leads by Activation", NOT "Total Leads",
 * which is the separate globally-deduped headline KPI — LRPO-5201). — LRPO-5134
 *
 * Mounted on the `.position-relative` wrapper around a Chartkick donut. The
 * center counter is a server-rendered CSS overlay (`.kpi-value`), not a
 * Chart.js plugin. Chartkick renders Chart.js into `#<chartId>` and registers
 * the chart in `window.Chartkick.charts[chartId]`; the underlying Chart.js
 * instance is reachable via `.getChartObject()`.
 *
 * Bug it fixes: clicking a legend entry toggles a slice's visibility (Chart.js
 * default doughnut legend onClick), but the static overlay kept showing the
 * full server total. We COMPOSE the legend `onClick` — the original toggle
 * still runs (slice hides/shows as before), then we recompute the visible sum
 * and write it into the overlay. The server-rendered value stays the baseline;
 * we only touch the overlay on a toggle.
 *
 *   data-controller="donut-total-overlay"
 *   data-donut-total-overlay-chart-id-value="leads-pie-chart"
 *   data-donut-total-overlay-target="kpiValue"   (on the .kpi-value div)
 *
 * Resilience:
 *   - Chartkick renders async; if the chart isn't in the registry yet we
 *     retry via requestAnimationFrame, bounded so we never spin forever.
 *   - Turbo cache: cancel any pending retry on disconnect; the chart itself
 *     is owned by Chartkick's own Turbo lifecycle (chartkick_turbo_lifecycle.js
 *     / reports_page_controller.js) — we never destroy it here.
 */
const MAX_READY_ATTEMPTS = 60 // ~1s at 60fps before we give up cleanly

export default class extends Controller {
  static targets = ["kpiValue"]
  static values = {
    chartId: { type: String, default: "leads-pie-chart" }
  }

  connect() {
    this._attempts = 0
    this._rafId = null
    this._renderRafId = null
    this._tryAttach()

    // LRPO-5188: the accessible HTML legend (chart_legend_controller) lives
    // inside this wrapper and dispatches a bubbling `chart-legend:toggled`
    // event whenever a slice is toggled via keyboard or mouse. Recompute the
    // center "Leads by Activation" on that event so the aria-live region announces the
    // new value for KEYBOARD/SR users too — the canvas-legend onClick path
    // (_composeLegendOnClick) only fires for a canvas legend, which the
    // dashboard donut no longer renders (legend.display=false).
    //
    // We ALWAYS re-resolve the chart fresh here (not just when _chart is
    // falsy): a Chartkick rebuild can leave us holding a truthy-but-DESTROYED
    // Chart.js 4 instance that still answers .data/.getDataVisibility with
    // STALE values. Resolving every time keeps us symmetric with
    // chart_legend_controller._toggle (which also re-resolves on each toggle).
    this._onLegendToggled = () => {
      const fresh = this._resolveChart()
      if (fresh) this._chart = fresh
      this._recompute()
    }
    this.element.addEventListener("chart-legend:toggled", this._onLegendToggled)

    // LRPO-5188 ghost-rebuild resilience: chartkick_turbo_lifecycle.js rebuilds
    // the dashboard donut IN PLACE on turbo:render (after Chartkick.destroyAll)
    // for redirect-chain / masquerade / back-forward visits — WITHOUT a Stimulus
    // disconnect. The rebuilt chart is all-visible, so our captured _chart is
    // dead and the center total is stale. Re-sync on turbo:render.
    //
    // CRITICAL: defer the re-resolve to requestAnimationFrame. The lifecycle's
    // rebuild runs synchronously inside the SAME turbo:render dispatch, so the
    // listener ordering is a race — but by the next frame the rebuilt chart is
    // guaranteed to exist in the registry regardless of which listener ran
    // first. A bare (synchronous) handler could read the registry before the
    // rebuild and recompute against a dead/absent chart.
    this._onTurboRender = () => {
      if (this._renderRafId != null) window.cancelAnimationFrame(this._renderRafId)
      this._renderRafId = window.requestAnimationFrame(() => {
        this._renderRafId = null
        const fresh = this._resolveChart()
        if (!fresh) return
        this._chart = fresh
        this._recompute()
      })
    }
    document.addEventListener("turbo:render", this._onTurboRender)
  }

  disconnect() {
    if (this._rafId != null) {
      window.cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
    if (this._renderRafId != null) {
      window.cancelAnimationFrame(this._renderRafId)
      this._renderRafId = null
    }
    if (this._onLegendToggled) {
      this.element.removeEventListener("chart-legend:toggled", this._onLegendToggled)
      this._onLegendToggled = null
    }
    if (this._onTurboRender) {
      document.removeEventListener("turbo:render", this._onTurboRender)
      this._onTurboRender = null
    }
    this._chart = null
  }

  // Poll the Chartkick registry until the Chart.js instance exists, then
  // compose the legend onClick. Bails cleanly after MAX_READY_ATTEMPTS so a
  // never-rendered chart (e.g. no-data branch) doesn't spin forever.
  _tryAttach() {
    const chart = this._resolveChart()
    if (chart) {
      this._chart = chart
      this._composeLegendOnClick()
      return
    }
    if (this._attempts >= MAX_READY_ATTEMPTS) return
    this._attempts += 1
    this._rafId = window.requestAnimationFrame(() => this._tryAttach())
  }

  // Reach the underlying Chart.js instance from Chartkick's registry. Returns
  // null (no throw) if Chartkick, the registry entry, or getChartObject is
  // not present yet.
  _resolveChart() {
    const registry = window.Chartkick && window.Chartkick.charts
    const entry = registry && registry[this.chartIdValue]
    if (!entry || typeof entry.getChartObject !== "function") return null
    return entry.getChartObject() || null
  }

  // Wrap the existing legend onClick so the default slice toggle still runs,
  // then recompute the center total.
  //
  // Idempotency guard: we tag the wrapper we install with a `_donutComposed`
  // flag and early-return if the current onClick already carries it. Without
  // the guard a second compose (e.g. a Turbo soft-render that reconnects this
  // controller WITHOUT a disconnect) would capture our own first wrapper as
  // `original` and nest it — the real toggle would still fire once, but
  // _recompute() would run twice per click (and a restore could flip
  // visibility twice). The guard makes re-composing a true no-op: same
  // handler, no extra chart.update().
  _composeLegendOnClick() {
    const chart = this._chart
    if (!chart) return
    // Walk down to options.plugins.legend, creating each level if Chart.js
    // hasn't populated it yet, then capture the existing handler to compose.
    const plugins = chart.options.plugins || (chart.options.plugins = {})
    const legend = plugins.legend || (plugins.legend = {})
    const original = legend.onClick
    // Already composed (re-attach without disconnect): no-op so we never
    // stack wrappers or re-run update().
    if (typeof original === "function" && original._donutComposed) return
    const self = this
    const wrapper = function (event, legendItem, legendObj) {
      if (typeof original === "function") {
        // Preserve Chart.js's `this` (the legend) and arg order.
        original.call(this, event, legendItem, legendObj)
      }
      self._recompute()
    }
    // Non-enumerable marker so it doesn't leak into option serialization /
    // Chart.js cloning, but is still readable on the captured reference above.
    Object.defineProperty(wrapper, "_donutComposed", { value: true })
    legend.onClick = wrapper
    if (typeof chart.update === "function") chart.update()
  }

  // Visible total = sum of dataset-0 values at indices Chart.js reports as
  // visible. Doughnut visibility in Chart.js v3 is per data-index.
  _recompute() {
    const chart = this._chart
    if (!chart || !this.hasKpiValueTarget) return
    const dataset = chart.data && chart.data.datasets && chart.data.datasets[0]
    const data = (dataset && dataset.data) || []

    let total = 0
    for (let i = 0; i < data.length; i++) {
      const visible = typeof chart.getDataVisibility === "function"
        ? chart.getDataVisibility(i)
        : true
      if (visible) total += Number(data[i]) || 0
    }

    this.kpiValueTarget.textContent = this._format(total)
  }

  // Format an integer with thousands separators to MATCH the server-rendered
  // baseline produced by ReportsHelper#report_number (app/helpers/reports_helper.rb).
  // The hardcoded "en-US" locale is deliberate: report_number emits plain
  // comma grouping, so this must stay locale-pinned to keep the JS-updated
  // value visually identical to the server value. KEEP IN SYNC with
  // ReportsHelper#report_number — if its grouping/locale changes, change here too.
  _format(value) {
    return Math.round(Number(value) || 0).toLocaleString("en-US")
  }
}
